import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Card, Form, Row, Col, Spinner, Badge } from "react-bootstrap";
import Select from 'react-select';
import Swal from "sweetalert2";
import { BASE_URL } from "../../services/Api";
import { authHeader } from "../../services/auth-header";

const CustomerView = () => {
  const { id } = useParams();
  const { pointindex } = useParams();
  const navigate = useNavigate();
  const [customerData, setCustomerData] = useState({
    companyname: "",
    contactperson: "",
    contactno: "",
    gstno: "",
    email: "",
    packingandforwarding: "",
    paymentterm: '',
    freight: "",
    incoterms: ""
  });

  const [addresses, setAddresses] = useState([{
    customeraddress: "",
    country: null,
    state: null,
    city: null,
    stateOptions: [],
    cityOptions: [],
    statecode: "",
    pincode: "",
  }]);

  const [countryOptions, setCountryOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch countries on component mount
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await axios.get(`${BASE_URL}country/findAllCountry`, {
          headers: authHeader(),
        });
        setCountryOptions(response.data.map(country => ({ value: country.id, label: country.countryname })));
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to fetch countries. Please try again.',
        });
      }
    };

    const fetchCustomer = async () => {
      if (id) {
        try {
          const response = await axios.get(`${BASE_URL}customer/findById/${id}`, {
            headers: authHeader(),
          });
          const data = response.data;

          setCustomerData({
            companyname: data.companyname,
            contactperson: data.contactperson,
            contactno: data.contactno,
            gstno: data.gstno,
            email: data.email,
            packingandforwarding: data.packingandforwarding,
            paymentterm: data.paymentterm,
            freight: data.freight,
            incoterms: data.incoterms
          });

          const customerAddresses = data.customerMultipleAddress.map(addr => ({
            customeraddress: addr.customeraddress,
            country: addr.country ? { value: addr.country.id, label: addr.country.countryname } : null,
            state: addr.state ? { value: addr.state.id, label: addr.state.statename, code: addr.state.statecode } : null,
            city: addr.city ? { value: addr.city.id, label: addr.city.cityname } : null,
            stateOptions: [],
            cityOptions: [],
            statecode: addr.statecode,
            pincode: addr.pincode,
          }));

          setAddresses(customerAddresses);
        } catch (error) {
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'Failed to fetch customer details. Please try again.',
          });
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchCountries();
    fetchCustomer();
  }, [id]);

  const getItem = (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        axios.delete(BASE_URL + "customer/delete/" + id, {
          headers: authHeader(),
        })
          .then(response => {
            if (response.data.length === 0) {
              Swal.fire(
                'Warning',
                'Customer present in another module. Delete operation has been cancelled.',
                'warning'
              );
            } else {
              navigate(`/customerindex/${pointindex}`);
              Swal.fire(
                'Deleted!',
                'Customer deleted successfully.',
                'success'
              );
            }
          })
          .catch((error) => {
            Swal.fire(
              'Error',
              error.response.data,
              'error'
            );
          });
      }
    });
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <div className="container-fluid" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      <div className="row">
        <div className="col-md-12">
          <Card className="shadow-sm" style={{ border: 'none' }}>
            <Card.Header className="d-flex justify-content-between align-items-center" style={{ backgroundColor: '#2c3e50', color: 'white' }}>
              <h5 className="mb-0">
                <i className="fas fa-user-tie mr-2"></i>
                Customer Details
              </h5>
              <div>
                <Link to={`/contactTable/${id}/${pointindex}`} className="button-70" style={{ backgroundColor: '#4e73df', color: 'white' }}>
                  <i className="fas fa-user-plus mr-1"></i> Add Contact
                </Link>
                <button onClick={() => getItem(id)} className="button-70" style={{ backgroundColor: '#e74a3b', color: 'white' }}>
                  <i className="fas fa-trash mr-1"></i> Delete
                </button>
                <Link to={`/CustomerEdit/${id}/${pointindex}`} className="button-70" style={{ backgroundColor: '#f6c23e', color: '#212529' }}>
                  <i className="fas fa-edit mr-1"></i> Edit
                </Link>
                <Link to={`/customerindex/${pointindex}`} className="button-70" style={{ backgroundColor: '#e9ecef', color: '#212529' }}>
                  <i className="fas fa-arrow-left mr-1"></i> Back
                </Link>
              </div>
            </Card.Header>
            <Card.Body style={{ backgroundColor: '#ffffff' }}>
              <div className="mb-4">
                <h6 className="border-bottom pb-2 mb-3">
                  <i className="fas fa-building mr-2"></i>Basic Information
                </h6>
                <Row>
                  <Col md={4} className="mb-3">
                    <Form.Group>
                      <Form.Label className="font-weight-bold">Company Name</Form.Label>
                      <Form.Control plaintext readOnly value={customerData.companyname || "N/A"} />
                    </Form.Group>
                  </Col>
                  <Col md={4} className="mb-3">
                    <Form.Group>
                      <Form.Label className="font-weight-bold">Contact Person</Form.Label>
                      <Form.Control plaintext readOnly value={customerData.contactperson || "N/A"} />
                    </Form.Group>
                  </Col>
                  <Col md={4} className="mb-3">
                    <Form.Group>
                      <Form.Label className="font-weight-bold">Mobile No.</Form.Label>
                      <Form.Control plaintext readOnly value={customerData.contactno || "N/A"} />
                    </Form.Group>
                  </Col>
                  <Col md={4} className="mb-3">
                    <Form.Group>
                      <Form.Label className="font-weight-bold">Email</Form.Label>
                      <Form.Control plaintext readOnly value={customerData.email || "N/A"} />
                    </Form.Group>
                  </Col>
                  <Col md={4} className="mb-3">
                    <Form.Group>
                      <Form.Label className="font-weight-bold">GST No.</Form.Label>
                      <Form.Control plaintext readOnly value={customerData.gstno || "N/A"} />
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              <div className="mb-4">
                <h6 className="border-bottom pb-2 mb-3">
                  <i className="fas fa-percentage mr-2"></i>Pricing Terms
                </h6>
                <Row>
                  <Col md={4} className="mb-3">
                    <Form.Group>
                      <Form.Label className="font-weight-bold">Packing & Forwarding (%)</Form.Label>
                      <Form.Control plaintext readOnly value={customerData.packingandforwarding || "0"} />
                    </Form.Group>
                  </Col>
                  <Col md={4} className="mb-3">
                    <Form.Group>
                      <Form.Label className="font-weight-bold">Freight (%)</Form.Label>
                      <Form.Control plaintext readOnly value={customerData.freight || "0"} />
                    </Form.Group>
                  </Col>
                  <Col md={4} className="mb-3">
                    <Form.Group>
                      <Form.Label className="font-weight-bold">Payment Terms</Form.Label>
                      <Form.Control plaintext readOnly value={customerData.paymentterm || "N/A"} />
                    </Form.Group>
                  </Col>
                  <Col md={4} className="mb-3">
                    <Form.Group>
                      <Form.Label className="font-weight-bold">Inco Terms</Form.Label>
                      <Form.Control plaintext readOnly value={customerData.incoterms || "N/A"} />
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              <div className="mb-4">
                <h6 className="border-bottom pb-2 mb-3">
                  <i className="fas fa-map-marker-alt mr-2"></i>Address Details
                </h6>
                {addresses.map((addr, index) => (
                  <Card key={index} className="mb-3 border">
                    <Card.Header className="bg-light d-flex justify-content-between">
                      <span className="font-weight-bold">Address #{index + 1}</span>
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        <Col md={12} className="mb-3">
                          <Form.Group>
                            <Form.Label className="font-weight-bold">Address Line</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={2}
                              plaintext
                              readOnly
                              value={addr.customeraddress || "N/A"}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={4} className="mb-3">
                          <Form.Group>
                            <Form.Label className="font-weight-bold">Country</Form.Label>
                            <Select
                              value={addr.country}
                              options={countryOptions}
                              isDisabled
                              placeholder="Not specified"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={4} className="mb-3">
                          <Form.Group>
                            <Form.Label className="font-weight-bold">State</Form.Label>
                            <Select
                              value={addr.state}
                              options={addr.stateOptions}
                              isDisabled
                              placeholder="Not specified"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={4} className="mb-3">
                          <Form.Group>
                            <Form.Label className="font-weight-bold">State Code</Form.Label>
                            <Form.Control plaintext readOnly value={addr.statecode || "N/A"} />
                          </Form.Group>
                        </Col>
                        <Col md={4} className="mb-3">
                          <Form.Group>
                            <Form.Label className="font-weight-bold">City</Form.Label>
                            <Select
                              value={addr.city}
                              options={addr.cityOptions}
                              isDisabled
                              placeholder="Not specified"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={4} className="mb-3">
                          <Form.Group>
                            <Form.Label className="font-weight-bold">Pincode</Form.Label>
                            <Form.Control plaintext readOnly value={addr.pincode || "N/A"} />
                          </Form.Group>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                ))}
              </div>
            </Card.Body>

            <Card.Footer style={{ backgroundColor: '#f8f9fa', borderTop: '1px solid #e9ecef' }}>
              <div className="d-flex justify-content-end">
                <Link to={`/customerindex/${pointindex}`} className="btn mr-2" style={{ backgroundColor: '#6c757d', color: 'white' }}>
                  <i className="fas fa-arrow-left mr-1"></i> Back to List
                </Link>
                <Link to={`/CustomerEdit/${id}/${pointindex}`} className="btn" style={{ backgroundColor: '#4e73df', color: 'white' }}>
                  <i className="fas fa-edit mr-1"></i> Edit Customer
                </Link>
              </div>
            </Card.Footer>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CustomerView;
